import type { TabItem } from '@skupreme/shared/ui';

export type OrderStatusTab = { status: string; label: string };

export const ORDER_STATUS_TABS: OrderStatusTab[] = [
  { status: 'Pending Shipment', label: 'Pending Shipment' },
  { status: 'Pending Fulfillment', label: 'Pending Fulfillment' },
  { status: 'Shipped', label: 'Shipped' },
  { status: 'Returns', label: 'Returns' },
  { status: 'Canceled', label: 'Canceled' },
];

/*
 * EN-4403: the new orders page's four states. The ids stay the legacy statuses
 * (paths, the API's status, permissions, links); only the labels change. No
 * Returns tab, and Held is a filter (hold=exception), not a tab.
 */
export const V2_ORDER_STATUS_TABS: OrderStatusTab[] = [
  { status: 'Pending Shipment', label: 'Unfulfilled' },
  { status: 'Pending Fulfillment', label: 'In Progress' },
  { status: 'Shipped', label: 'Shipped' },
  { status: 'Canceled', label: 'Historical' },
];

/* EN-4403: the new page's word for each page state (`view_state`). */
export const VIEW_STATE_LABELS: Record<string, string> = {
  unfulfilled: 'Unfulfilled',
  in_progress: 'In Progress',
  shipped: 'Shipped',
  historical: 'Historical',
};

/*
 * EN-4204: orders waiting for an operator. Only the V2 API serves this status,
 * and its status counts leave it out.
 */
export const HELD_STATUS = 'held';
export const HELD_TAB: OrderStatusTab = { status: HELD_STATUS, label: 'Held' };

export type StatusCountRow = {
  fulfillment_status?: string | null;
  count?: number | string | null;
};

/* A status missing from the rows was not counted, so its tab shows no capsule. */
export function getStatusCounts(
  rows: StatusCountRow[] | undefined,
): Record<string, number> {
  const counts: Record<string, number> = {};

  for (const row of rows || []) {
    if (!row?.fulfillment_status) {
      continue;
    }

    counts[row.fulfillment_status] = Number(row.count) || 0;
  }

  return counts;
}

/* Statuses carry spaces, so they are encoded in the path. */
export function getStatusHref(status: string): string {
  return `/app/orders/list/${encodeURIComponent(status)}/`;
}

/* Only the first segment: a batch or a sub-tab may follow the status. */
export function getStatusFromPath(pathname: string): string {
  const match = pathname.match(/\/app\/orders\/list\/([^/]+)/);

  return match ? decodeURIComponent(match[1]) : '';
}

/*
 * Queues an order has already left. Their size is a running total that only
 * grows, so it says nothing about work waiting, and the capsule is left off.
 * The held tab is never counted at all (EN-4204).
 */
const NO_COUNT_STATUSES = new Set([
  'Shipped',
  'Returns',
  'Canceled',
  HELD_STATUS,
]);

export function getStatusTabItems(
  counts: Record<string, number>,
  tabs: OrderStatusTab[] = ORDER_STATUS_TABS,
): (TabItem & { id: string })[] {
  return tabs.map(tab => {
    const count = counts[tab.status];
    const showsCount = !NO_COUNT_STATUSES.has(tab.status) && count > 0;

    return {
      id: tab.status,
      label: tab.label,
      count: showsCount ? count : undefined,
      href: getStatusHref(tab.status),
    };
  });
}

/*
 * EN-4117: the number in the page title. A V2 page reads its own counts (null
 * on legacy) and, while counting or after a failure, never the sidebar's.
 */
export function getStatusTitleCount(
  status: string | undefined,
  v2Counts: { rows: StatusCountRow[] | undefined; loading: boolean } | null,
  sidebarRows: StatusCountRow[] | undefined,
): number | string | undefined {
  if (v2Counts) {
    if (v2Counts.loading) return 'Loading';
    if (!v2Counts.rows || !status) return undefined;

    return getStatusCounts(v2Counts.rows)[status] ?? 0;
  }

  const row = (sidebarRows || []).find(x => x.fulfillment_status === status);

  return row?.count || 'Loading';
}

export function getOrderListTitle(
  brandName: string,
  count: number | string | undefined,
): string {
  return `${brandName} - Order List${count === undefined ? '' : ` (${count})`}`;
}
